import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useGetUsersQuery } from './usersApi';
import { Users as UsersIcon, UserPlus, UserMinus, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import type { User } from '@/types/users.types';
import { Link } from 'react-router-dom';

const limit = 10;

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getStatusColor = (isActive: boolean) =>
  isActive ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200';

export const Users = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, error } = useGetUsersQuery({
    page: currentPage,
    limit,
    text: searchText || undefined,
  });

  const totalPages = data ? Math.ceil(data.totalUsers / limit) : 0;

  const handleSearch = () => {
    setSearchText(searchInput);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchText('');
    setSearchInput('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    handleSearch();
  };

  return (
    <>
      {/* Users Overview */}
      <div className='mb-8'>
        <h2 className='text-3xl font-bold text-white mb-2'>User Management</h2>
        <p className='text-slate-400'>Manage users, roles, and permissions across your organization.</p>
      </div>

      {/* User Stats and Search */}
      <div className='grid gap-6 md:grid-cols-3 mb-6'>
        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-slate-400'>Total Users</p>
                <p className='text-3xl font-bold text-white'>{isLoading ? '...' : (data?.totalUsers ?? 0)}</p>
              </div>
              <UsersIcon className='h-8 w-8 text-slate-400' />
            </div>
          </CardContent>
        </Card>

        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-slate-400'>Active Users</p>
                <p className='text-3xl font-bold text-white'>{isLoading ? '...' : (data?.activeUsers ?? 0)}</p>
              </div>
              <UserPlus className='h-8 w-8 text-green-400' />
            </div>
          </CardContent>
        </Card>

        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center gap-3'>
              <Input
                placeholder='Search users...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyUp={handleKeyPress}
                onKeyDown={handleKeyPress}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
              />
              <Button onClick={handleSearch} size='sm' disabled={isLoading} className='flex-shrink-0'>
                <Search className='h-4 w-4' />
              </Button>
              {searchText && (
                <Button onClick={handleClearSearch} variant='outline' size='sm' className='flex-shrink-0'>
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className='bg-slate-800 border-slate-700'>
        <CardHeader>
          <CardTitle className='text-white'>
            Users List
            {searchText && (
              <span className='text-sm font-normal text-slate-400 ml-2'>(filtered by "{searchText}")</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-slate-400'>Loading users...</div>
            </div>
          ) : error ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-red-400'>Error loading users</div>
            </div>
          ) : !data?.users?.length ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-slate-400'>
                {searchText ? 'No users found matching your search' : 'No users found'}
              </div>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className='overflow-x-auto'>
                <table className='w-full'>
                  <thead>
                    <tr className='border-b border-slate-700'>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Name</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Email</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Status</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Role</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Account ID</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((user: User) => (
                      <tr key={user._id} className='border-b border-slate-700/50 hover:bg-slate-700/25'>
                        <td className='py-4 px-2'>
                          <div>
                            <div className='text-white font-medium'>
                              {user.firstName} {user.lastName}
                            </div>
                            <div className='text-sm text-slate-400'>{user._id}</div>
                          </div>
                        </td>
                        <td className='py-4 px-2'>
                          <div className='text-slate-300'>{user.email}</div>
                        </td>
                        <td className='py-4 px-2'>
                          <Badge className={getStatusColor(user.isActive)}>
                            {user.isActive ? 'active' : 'inactive'}
                          </Badge>
                        </td>
                        <td className='py-4 px-2'>
                          <div className='text-slate-300'>{user.role}</div>
                        </td>
                        <td className='py-4 px-2'>
                          <Link to={`/accounts?companyId=${user.account}`} className='text-blue-400 hover:underline'>
                            {user.account}
                          </Link>
                        </td>
                        <td className='py-4 px-2'>
                          <div className='text-slate-300'>{formatDate(user.createdAt)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className='flex items-center justify-between mt-6 pt-4 border-t border-slate-700'>
                  <div className='text-sm text-slate-400'>
                    Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, data.totalUsers)} of{' '}
                    {data.totalUsers} users
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage <= 1 || isLoading}
                    >
                      <ChevronLeft className='h-4 w-4' />
                      Previous
                    </Button>

                    <div className='flex items-center gap-1'>
                      {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                          return (
                            <Button
                              key={page}
                              variant={currentPage === page ? 'default' : 'outline'}
                              size='sm'
                              onClick={() => setCurrentPage(page)}
                              disabled={isLoading}
                              className='w-8 h-8 p-0'
                            >
                              {page}
                            </Button>
                          );
                        } else if (page === currentPage - 3 || page === currentPage + 3) {
                          return (
                            <span key={page} className='text-slate-400'>
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>

                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage >= totalPages || isLoading}
                    >
                      Next
                      <ChevronRight className='h-4 w-4' />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};
